import type { ReactNode } from 'react';
import { Browser } from '@capacitor/browser';
import { useVersionCheck } from '@/hooks/useVersionCheck';
import { APP_STORE_URL_IOS, APP_STORE_URL_ANDROID } from '@/lib/constants';
import { T, RADIUS, SHADOW, SPACE } from '@/lib/tokens';

/**
 * Blocks the entire app with a non-dismissible update prompt when the
 * native app version is below the remotely-configured minimum.
 *
 * On web (or while the check is loading) renders children normally.
 */
export function ForceUpdateGate({ children }: { children: ReactNode }) {
  const { needsUpdate, platform } = useVersionCheck();

  if (!needsUpdate) {
    return <>{children}</>;
  }

  const storeUrl = platform === 'ios' ? APP_STORE_URL_IOS : APP_STORE_URL_ANDROID;

  const handleUpdate = async () => {
    await Browser.open({ url: storeUrl });
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center"
      style={{ background: T.bg, padding: SPACE.screenPadH }}
    >
      <div
        className="w-full max-w-[420px] px-6 py-8 text-center"
        role="alertdialog"
        aria-modal="true"
        aria-label="Update required"
        style={{
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: RADIUS.xl,
          boxShadow: SHADOW.lg,
        }}
      >
        <h1
          className="mb-2 text-lg font-semibold"
          style={{ color: T.text, letterSpacing: '-0.02em' }}
        >
          Update Required
        </h1>
        <p
          className="mb-6 text-sm leading-relaxed"
          style={{ color: T.textSec, lineHeight: '1.65' }}
        >
          A newer version of Share Shack is available. Please update to continue.
        </p>
        <button
          onClick={handleUpdate}
          className="w-full px-5 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          style={{
            background: T.accent,
            borderRadius: RADIUS.md,
            boxShadow: SHADOW.btn.accent,
          }}
        >
          Update Now
        </button>
      </div>
    </div>
  );
}
